import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Plus, 
  Edit3, 
  Copy, 
  Trash2, 
  CheckCircle2, 
  XCircle, 
  Sparkles, 
  Search,
  Filter,
  FileCheck,
  RotateCcw,
  Layers,
  Star
} from 'lucide-react';
import { DocumentoModelo, TipoDocumentoModelo } from '../../types/documentoModelo';
import { modeloDocumentoService, DEFAULT_MODELOS } from '../../services/modeloDocumentoService';

interface Props {
  onOpenEditor: (modelo: DocumentoModelo) => void;
}

export const GerenciadorModelos: React.FC<Props> = ({ onOpenEditor }) => {
  const [modelos, setModelos] = useState<DocumentoModelo[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState<'todos' | TipoDocumentoModelo>('todos');

  // Modal Novo Modelo State
  const [isModalNewOpen, setIsModalNewOpen] = useState(false);
  const [novoNome, setNovoNome] = useState('');
  const [novoTipo, setNovoTipo] = useState<TipoDocumentoModelo>('ordem_servico');
  const [novaDescricao, setNovaDescricao] = useState('');
  const [usarPadraoPreenchido, setUsarPadraoPreenchido] = useState(true);

  const carregarModelos = async () => {
    setLoading(true);
    try {
      const data = await modeloDocumentoService.getModelos();
      setModelos(data);
    } catch (err) {
      console.error('Erro ao carregar modelos:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarModelos();
  }, []);

  const handleRestaurarPadroes = async () => {
    if (confirm('Deseja restaurar os modelos padrão de Ordem de Serviço e Proposta Comercial (Multi-página) para as versões oficiais preenchidas?')) {
      setLoading(true);
      try {
        const novos = await modeloDocumentoService.restaurarModelosPadrao();
        setModelos(novos);
      } catch (err) {
        console.error('Erro ao restaurar padrões:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCriarModelo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!novoNome.trim()) return;

    try {
      let initialJson = JSON.stringify({
        version: '5.3.0',
        objects: [
          {
            type: 'i-text',
            left: 50,
            top: 50,
            text: novoNome.toUpperCase(),
            fontSize: 24,
            fontWeight: 'bold',
            fill: '#0f172a'
          }
        ]
      });

      let paginasCount = 1;

      if (usarPadraoPreenchido) {
        const def = DEFAULT_MODELOS.find(m => m.tipo === novoTipo);
        if (def) {
          initialJson = def.canvas_json;
          paginasCount = def.paginas_count || 1;
        }
      }

      const novomodelo = await modeloDocumentoService.salvarModelo({
        nome: novoNome.trim(),
        tipo: novoTipo,
        descricao: novaDescricao.trim(),
        versao: 1,
        ativo: true,
        largura_px: 794,
        altura_px: 1123,
        paginas_count: paginasCount,
        canvas_json: initialJson
      });

      setIsModalNewOpen(false);
      setNovoNome('');
      setNovaDescricao('');
      await carregarModelos();
      onOpenEditor(novomodelo);
    } catch (err) {
      console.error('Erro ao criar modelo:', err);
    }
  };

  const handleDuplicar = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await modeloDocumentoService.duplicarModelo(id);
      await carregarModelos();
    } catch (err) {
      console.error('Erro ao duplicar modelo:', err);
    }
  };

  const handleExcluir = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Tem certeza que deseja excluir este modelo de documento?')) {
      try {
        const ok = await modeloDocumentoService.excluirModelo(id);
        if (!ok) {
          alert('Não foi possível excluir o modelo no servidor. Ele continua disponível para os demais usuários — tente novamente.');
          return;
        }
        await carregarModelos();
      } catch (err) {
        console.error('Erro ao excluir modelo:', err);
        alert('Erro inesperado ao excluir o modelo.');
      }
    }
  };

  const handleDefinirPadrao = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const ok = await modeloDocumentoService.definirComoPadrao(id);
      if (!ok) {
        alert('Não foi possível definir este modelo como padrão.');
        return;
      }
      await carregarModelos();
    } catch (err) {
      console.error('Erro ao definir modelo padrão:', err);
      alert('Erro ao definir este modelo como padrão.');
    }
  };

  const handleToggleAtivo = async (modelo: DocumentoModelo, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await modeloDocumentoService.salvarModelo({
        ...modelo,
        ativo: !modelo.ativo
      });
      await carregarModelos();
    } catch (err) {
      console.error('Erro ao alterar status:', err);
    }
  };

  const modelosFiltrados = modelos.filter((m) => {
    const bateuTipo = tipoFiltro === 'todos' || m.tipo === tipoFiltro;
    const bateuBusca = !searchTerm || m.nome.toLowerCase().includes(searchTerm.toLowerCase()) || (m.descricao || '').toLowerCase().includes(searchTerm.toLowerCase());
    return bateuTipo && bateuBusca;
  });

  return (
    <div className="space-y-6">
      {/* Top Banner & Action */}
      <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-red-600" />
            Modelos de Documentos Personalizados
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Crie e edite visualmente os modelos padrão de Ordens de Serviço e Propostas Comerciais Multi-página no editor Canva.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleRestaurarPadroes}
            className="px-3.5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold flex items-center gap-1.5 transition active:scale-95 border border-slate-200"
            title="Restaurar os modelos padrão originais preenchidos"
          >
            <RotateCcw className="w-3.5 h-3.5 text-slate-500" />
            <span>Restaurar Padrões</span>
          </button>

          <button
            onClick={() => setIsModalNewOpen(true)}
            className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-md shadow-red-600/20 transition active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span>+ Criar Novo Modelo</span>
          </button>
        </div>
      </div>

      {/* Filter & Search Bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar modelo por nome..."
            className="w-full pl-9 pr-3 py-2 text-xs bg-white border border-slate-200 rounded-lg outline-none focus:border-red-500 font-medium"
          />
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-3.5 h-3.5 text-slate-400" />
          <select
            value={tipoFiltro}
            onChange={(e) => setTipoFiltro(e.target.value as any)}
            className="text-xs bg-white border border-slate-200 rounded-lg px-3 py-2 outline-none font-medium text-slate-700"
          >
            <option value="todos">Todos os Tipos</option>
            <option value="ordem_servico">Ordem de Serviço</option>
            <option value="proposta">Proposta Comercial</option>
            <option value="contrato">Contrato</option>
            <option value="recibo">Recibo</option>
          </select>
        </div>
      </div>

      {/* Grid of Templates */}
      {loading ? (
        <div className="py-12 text-center space-y-3">
          <div className="w-8 h-8 border-3 border-red-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs text-slate-500 font-medium">Carregando modelos de documentos...</p>
        </div>
      ) : modelosFiltrados.length === 0 ? (
        <div className="p-12 text-center bg-white rounded-2xl border border-slate-200/80 space-y-3">
          <FileCheck className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="font-bold text-slate-800 text-sm">Nenhum modelo encontrado</h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            Não encontramos modelos para os filtros selecionados. Clique em "Restaurar Padrões" para carregar as opções preenchidas VOOPE.
          </p>
          <button
            onClick={handleRestaurarPadroes}
            className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold transition hover:bg-slate-800"
          >
            Carregar Modelos Padrão VOOPE
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {modelosFiltrados.map((item) => (
            <div
              key={item.id}
              onClick={() => onOpenEditor(item)}
              className="group bg-white rounded-2xl border border-slate-200/80 hover:border-red-500/50 shadow-sm hover:shadow-xl transition-all duration-200 overflow-hidden cursor-pointer flex flex-col justify-between"
            >
              <div className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className={`p-2 rounded-xl ${item.tipo === 'proposta' ? 'bg-blue-50 text-blue-600' : 'bg-red-50 text-red-600'}`}>
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                        {item.tipo.replace('_', ' ')}
                      </span>
                      <h3 className="font-bold text-slate-900 text-sm group-hover:text-red-600 transition">
                        {item.nome}
                      </h3>
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                      item.ativo ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-500'
                    }`}>
                      {item.ativo ? <CheckCircle2 className="w-3 h-3 text-emerald-600" /> : <XCircle className="w-3 h-3 text-slate-400" />}
                      <span>{item.ativo ? 'Ativo' : 'Inativo'}</span>
                    </span>

                    {item.padrao && (
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200"
                        title="Este é o modelo usado ao gerar o documento deste tipo"
                      >
                        <Star className="w-3 h-3 text-amber-500" />
                        <span>Padrão</span>
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">
                  {item.descricao || 'Modelo visual personalizável para documentos oficiais VOOPE.'}
                </p>
              </div>

              {/* Card Footer */}
              <div className="px-5 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-[11px] font-bold bg-white px-2 py-0.5 rounded border border-slate-200 text-slate-700">
                    v{item.versao}
                  </span>
                  <span className="text-[11px] font-semibold text-slate-500 flex items-center gap-1">
                    <Layers className="w-3 h-3 text-slate-400" />
                    {item.paginas_count || 1} {item.paginas_count === 1 ? 'página' : 'páginas'}
                  </span>
                </div>

                <div className="flex items-center gap-1.5 opacity-90 group-hover:opacity-100 transition">
                  {!item.padrao && (
                    <button
                      onClick={(e) => handleDefinirPadrao(item.id, e)}
                      className="p-1.5 rounded-lg hover:bg-amber-100 text-slate-500 hover:text-amber-600 transition"
                      title="Usar este modelo ao gerar os documentos deste tipo"
                    >
                      <Star className="w-4 h-4" />
                    </button>
                  )}

                  <button
                    onClick={(e) => handleToggleAtivo(item, e)}
                    className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-600 transition"
                    title={item.ativo ? 'Desativar Modelo' : 'Ativar Modelo'}
                  >
                    {item.ativo ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-slate-400" />}
                  </button>

                  <button
                    onClick={(e) => handleDuplicar(item.id, e)}
                    className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-600 transition"
                    title="Duplicar Modelo"
                  >
                    <Copy className="w-4 h-4" />
                  </button>

                  <button
                    onClick={(e) => handleExcluir(item.id, e)}
                    className="p-1.5 rounded-lg hover:bg-rose-100 text-rose-600 transition"
                    title="Excluir Modelo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Criar Novo Modelo */}
      {isModalNewOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-5 animate-in fade-in zoom-in duration-200 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-50 text-red-600 rounded-xl">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-base">Novo Modelo de Documento</h3>
                <p className="text-xs text-slate-500">Defina o nome e o tipo do documento para iniciar o editor.</p>
              </div>
            </div>

            <form onSubmit={handleCriarModelo} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Nome do Modelo *</label>
                <input
                  type="text"
                  required
                  value={novoNome}
                  onChange={(e) => setNovoNome(e.target.value)}
                  placeholder="Ex: Proposta Comercial Executiva 2026"
                  className="w-full text-xs p-2.5 border border-slate-300 rounded-xl outline-none focus:border-red-500 font-medium"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Tipo de Documento *</label>
                <select
                  value={novoTipo}
                  onChange={(e) => setNovoTipo(e.target.value as TipoDocumentoModelo)}
                  className="w-full text-xs p-2.5 border border-slate-300 rounded-xl outline-none bg-white font-medium text-slate-800"
                >
                  <option value="ordem_servico">Ordem de Serviço</option>
                  <option value="proposta">Proposta Comercial</option>
                  <option value="contrato">Contrato</option>
                  <option value="recibo">Recibo</option>
                </select>
              </div>

              {/* Starter template option */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-2">
                <label className="text-xs font-bold text-slate-800 block">Conteúdo Inicial:</label>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
                    <input
                      type="radio"
                      name="starterOption"
                      checked={usarPadraoPreenchido}
                      onChange={() => setUsarPadraoPreenchido(true)}
                      className="text-red-600 focus:ring-red-500"
                    />
                    <span>Iniciar com Modelo Padrão Preenchido VOOPE</span>
                  </label>
                  <label className="flex items-center gap-2 text-xs font-medium text-slate-700 cursor-pointer">
                    <input
                      type="radio"
                      name="starterOption"
                      checked={!usarPadraoPreenchido}
                      onChange={() => setUsarPadraoPreenchido(false)}
                      className="text-red-600 focus:ring-red-500"
                    />
                    <span>Iniciar em Branco (1 Página Vazia)</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Descrição (Opcional)</label>
                <textarea
                  value={novaDescricao}
                  onChange={(e) => setNovaDescricao(e.target.value)}
                  placeholder="Breve descrição ou objetivo do modelo..."
                  rows={2}
                  className="w-full text-xs p-2.5 border border-slate-300 rounded-xl outline-none focus:border-red-500 resize-none font-medium"
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalNewOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-xl shadow-md transition"
                >
                  Criar e Abrir Editor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
